import TabEnabledMixin from '../../../moreCore/UI/Tabbing/_TabEnabledMixin';
import lang from 'dojo/_base/lang';
import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './pdfViewer.html?raw';
import * as pdfTypes from "./pdfTypes";

import * as SyncfusionHelper from "../../../moreCore/Tools/syncfusionHelper";
import { ExtraHelpers } from "../../../more/Helpers";

/**
 * Wrapper fur den Syncfusion pdfReader
 * [Docs](https://help.syncfusion.com/document-processing/pdf/pdf-viewer/javascript-es5/getting-started)
 *
 * Events mit "(experimental)" koennen evtl. bei Updates von SyncFusion implodieren
 *
 * @fires saveButton - Wenn der Speichern Button gedrueckt wird
 * @fires toggleFullscreen - Wenn der Fullscreen ein- oder ausgehschalten wird
 * @fires ready - Wenn die basis Sachen von Syncfusion fertig geladen haben
 * @fires pdfReady - Wenn der Pdf-Ready initialisiert wurde und mit dem Laden des Dokumentes startet
 * @fires pageChange - Wenn die Seite geaendert wird
 * @fires pageClick - Wenn auf eine Dokumenten Seite geclickt wird
 * @fires pageRenderInitiate - Wenn der PDf-Reader mit dem laden einer Dokumenten-Seite beginnt
 * @fires pageRenderCompleteFirstLoad - Wenn die erste Seite des Dokumentes erfolgreich geladen worden ist.
 * @fires pageRenderComplete - Wenn eine Seite erfolgreich geladen wurde (wird auch beim firstLoad getriggered). Anmerkung: triggered evtl. VOR dem laden von Annotations & Form Felder...
 * @fires zoomChange - Wenn die Zoom Einstellungen geaendert wurde
 * @fires propertyChanged - Wenn ein Pdf-Property sich geandert hat (annotations, form, etc...). Achtung: Event triggered tatsaechlich zu frueh im Doc load-in von Syncfusion aus, es koennten also Teile vom PdfViewer noch nicht geladen sein...
 * @fires annotationsChanged - Wenn eine Annotation sich geaendert hat
 * @fires formFieldAdd - Wenn ein Pdf-Form-Field hinzugefuegt wurde
 * @fires formFieldClick
 * @fires formFieldDoubleClick
 * @fires formFieldPropertiesChange
 * @fires formFieldRemove
 * @fires formFieldValidationFailed - Wenn das Validieren vom Pdf-Formular fehlgeschlagen ist
 * @fires toolbarClick - Wenn auf irgendwas in der Toolbar geclickt wurde
 * @fires downloadStart - Wenn der Pdf-Download startet (Browser Downloader oder Download zu base64-string)
 * @fires downloadEnd - Wenn der Pdf-Download fertig ist (evtl. nicht akkurat bei Browser Downloads wegen z.b. "Speichern als...")
 * @fires pageOrganizer_saveClick - Wenn im Page Organizer auf Save geclickt wird. (experimental)
 * @fires pageOrganizer_saveAsClick - Wenn im Page Organizer auf SaveAs geclickt wird. (experimental)
 * @fires annotation_import_export_menu_click - Disabled Import/Export Menue fuer Annotations (experimental)
 * @fires signatureSelect - Wenn eine Signatur ge-clickt wurde.
 * @fires signatureUnselect - Wenn eine Signatur ab-wegaehlt wurde.
 * @fires annotationAdd
 * @fires annotationMove
 * @fires annotationRemove
 * @fires annotationResize
 * @fires annotationPropertiesChange - Wenn eine bereits existierende Annotation geaendert wurde
 * @fires commentAdd
 * @fires commentDelete
 * @fires commentEdit
 * @fires commentStatusChanged
 * @fires addSignature
 * @fires removeSignature
 * @fires signaturePropertiesChange
 * @fires formFieldMove
 * @fires formFieldResize
 * @fires formFieldChanged - Wenn sich irgendwas an den formFields aendert...
 * @fires allEventsReady - Wenn alle Events geladen haben, und der Pdf-Reader bereit ist zum leben
*/
class pdfViewer extends compose(TabEnabledMixin) {
    templateString: any = template;

    /**
    * Configuration des Pdf-Viewers.
    * Die meisten Einstellungen hier werden nur vor dem Laden des Pdf-Viewers beruecksichtigt
    *
    * @property {pdfTypes.PdfViewerConfig} config - Haupt Config Objekt.
    * @property {object} ToolbarConfig - Konfiguration fuer alles in der Toolbar. Alle Unteroptionen setzt die Sichtbarkeit des Features.
    * @property {object} ToolbarCustomButtons - Hier koennen Custom-Buttons zur Toolbar hinzugefuegt werden. Siehe Beispiele im pdfViewer.ts
    * @property {object} stickyNotes - Einstellungen zu Dokument Notizen und Kommentare.
    * @property {string} stickyNotes.authorName - Anzeigename, Standart: `this.getAuthorName()`
    * @property {boolean} stickyNotes.enableStickyNotes - Ob ueberhaupt Notizen angezeigt werden.
    * @property {object} pageOrganizerSettings - Einstellungen fuer den PageOrganizer. Editiert das Originale Dokument das dann mit Download rundereladen oder als base64 String ausgelesen werden kann
    * @property {boolean} pageOrganizerSettings.enablePageOrganizer - Ob Page Organizer ueberhaupt verfuegbar ist. Im zweifel: Nein
    * @property {boolean} pageOrganizerSettings.canCopy - Ob Seiten Dupliziert werden koennen.
    * @property {boolean} pageOrganizerSettings.canDelete - Ob einzelne Seiten aus dem Dokument geloescht werden koennen.
    * @property {boolean} pageOrganizerSettings.canImport - Ob neue Seiten Importiert werden koennen. Der End-User kann dann Zeug hochladen.
    * @property {boolean} pageOrganizerSettings.canInsert - Ob Seiten eingefuegt werden koennen.
    * @property {boolean} pageOrganizerSettings.canRearrange - Seiten Reihfolge editieren.
    * @property {boolean} pageOrganizerSettings.canRotate - Rotieren von einzelnen Seiten.
    * @property {boolean} cancelDownloads - Ob Downloads immer abgebrochen werden. Dies unterbinden ALLE Downloads jeder Art.
    */
    public config: pdfTypes.PdfViewerConfig = {
        ToolbarConfig: {
            OpenOption: false,
            PageNavigationTool: true,
            MagnificationTool: true,
            PanTool: true,
            SelectionTool: true,
            SearchOption: true,
            PrintOption: true,
            DownloadOption: false,
            UndoRedoTool: true,
            AnnotationEditTool: true,
            FormDesignerEditTool: false,
            CommentTool: true,
            SubmitForm: false,
            SaveButton: false, //SaveButton ist CustomButton, siehe constructor
            ToggleFullScreen: false, //ToggleFullScreen ist auch CustomButton
            YouSignSignature:false
        },
        ToolbarCustomButtons: {}, //siehe loadPdfByBase64();
        stickyNotes: {
            authorName: "Guest", //wir setzen das hier erstmal auf "Guest", wenn der wert nicht "Guest" spaeter ist, dann ueberschreiben wir den mit dem username
            enableStickyNotes: true,
        },
        pageOrganizerSettings: {
            enablePageOrganizer: false,
            canCopy: false,
            canDelete: false,
            canImport: false,
            canInsert: false,
            canRearrange: false,
            canRotate: true
        },
        cancelDownloads: false,
        enableRightClickMenu:true,
        experimental: {}
    };

    /**
     * Aktueller Zustand des Dokumentes.
     */
    public documentState: pdfTypes.PdfDocumentStatus = {
        currentPage: null,
        maxPage: null,
        currentZoom: null,
        annotations: null,
        formFieldsJson: null,
    };

    public formFieldsExportDataFormats: object = {};
    private viewContainer: HTMLDivElement;
    private pdfViewerUID: string;

    private pdfViewer: pdfTypes.PdfViewer;

    private flagFirstLoad = true;
    private flagReady = false;
    private eventHookTimeout:any;

    /**
     * Ob die Basis Files fuer Syncfusion geladen haben
     */
    public isReady = false; //bitte nicht fuer interne logik verwenden...
    private waitForTrueInterval: any;
    private async initDependencys(callback: Function) {
        await Promise.allSettled([
            ExtraHelpers.sideLoadResource(SyncfusionHelper.getSfLangScriptPath(), 'js'),
            ExtraHelpers.sideLoadResource(window.appRoot + "Content/syncfusion/pdfReader/scripts/ej2.js", 'js'),
            ExtraHelpers.sideLoadResource(window.appRoot + "Content/syncfusion/pdfReader/styles/material.css", 'css'),
            ExtraHelpers.sideLoadResource(window.appRoot + "Content/syncfusion/pdfReader/styles/customized/material.css", 'css'),
            ExtraHelpers.sideLoadResource(window.appRoot + "Content/syncfusion/pdfReader/styles/material-dark.css", 'css'),
            ExtraHelpers.sideLoadResource(window.appRoot + "Content/syncfusion/pdfReader/styles/customized/material-dark.css", 'css'),
        ]);
        if (typeof callback == 'function') {
            callback();
        }
    }

    private async loadDependency(dependency: { url: string, type: string }) {
        switch (dependency.type) {
            case "css":
                return new Promise((resolve, reject) => {
                    let newCssElem = document.createElement("link");
                    newCssElem.setAttribute("rel", "stylesheet");
                    newCssElem.href = dependency.url;
                    newCssElem.onload = () => resolve(true);
                    newCssElem.onerror = () => reject(false);
                    document.head.appendChild(newCssElem);
                });
                break;
            case "js":
                return new Promise((resolve, reject) => {
                    let newCssElem = document.createElement("script");
                    newCssElem.src = dependency.url;
                    newCssElem.onload = () => resolve(true);
                    newCssElem.onerror = (e) => reject(e);
                    document.head.appendChild(newCssElem);
                });
                break;
        }
    }
    private flagIsFullscreen = false;
    private youSignSignatureState = false;
    postCreate(): void {
        this.pdfViewerUID = this.id + "_" + new Date().valueOf(); // :(
        this.viewContainer.id = this.pdfViewerUID;
        //custom buttons
        this.config.ToolbarCustomButtons["YouSignSignature"] = {
            buttonDefinition: {
                prefixIcon: 'e-icons e-rephrase YouSignBtnIsInactive',
                id: 'YouSignSignature',
                text: this.translate("PdfViewer__CustomButton_YouSign_Place"),
                tooltipText: this.translate("PdfViewer__CustomButton_YouSign_Place"),
                align: 'Left',
            },
            callback: lang.hitch(this, ()=>{
                this.emit("YouSignSignature");
                this.youSignSignatureState = ! this.youSignSignatureState;
                this.setYousignSignatureState(this.youSignSignatureState);
            })
        };
        this.config.ToolbarCustomButtons["SaveButton"] = { // siehe https://help.syncfusion.com/document-processing/pdf/pdf-viewer/javascript-es5/how-to/add-save-button-js
            buttonDefinition: {
                prefixIcon: 'e-icons e-save pdfEditorSaveIconButton',
                id: 'SaveButton',
                text: this.translate("General_Save"),
                tooltipText: this.translate("General_Save"),
                align: 'Right'
            },
            callback: lang.hitch(this, () => {
                this.emit("saveButton");
            }),
        };
        this.config.ToolbarCustomButtons["ToggleFullScreen"] = {
            buttonDefinition: {
                prefixIcon: 'e-icons e-full-screen-2',
                id: 'ToggleFullScreen',
                text: this.translate("General_FullScreen"),
                tooltipText: this.translate("General_FullScreen"),
                align: 'Right',
            },
            callback: lang.hitch(this, (ev: PointerEvent) => {
                this.flagIsFullscreen = !this.flagIsFullscreen;
                this.emit("toggleFullscreen", this.flagIsFullscreen);
                if (this.flagIsFullscreen) {
                    this.viewContainer.requestFullscreen({ navigationUI: 'auto' });
                } else {
                    document.exitFullscreen();
                }
            }),
        };
        try {
            this.initDependencys(lang.hitch(this, this.postCreate_Continue));
        } catch (err) {
            console.error("error loading dependencys", err);
            this.showToast(this.translate("General_Error") + ":" + "unable to initialize pdfViewer.", "fas fa-bug");
        }
    }

    private postCreate_Continue() {
        /*
        let lkey = this.getModule("Webcalendar")["license"];
        if (typeof window["syncfusionLkSet"] == 'undefined') {
            window["ej"]["base"].registerLicense(lkey);
            window["syncfusionLkSet"] = true;
        }
        */
        if (SyncfusionHelper.loadLicense(this.getModule("Webcalendar")["license"]) == false){
            this.showToast(this.translate("SyncfusionLicenseError"), "fas fa-bug");
        }

        if (SyncfusionHelper.loadLanguage() == false){
            console.error("syncfusionhelper: can't load translation");
            this.showToast("Syncfusion: can't load translation", "fas fa-bug");
        }

        this.flagReady = true;
        this.isReady = true;
        this.emit("ready");
    }

    private waitForReady(interval: number = 100): Promise<boolean> {
        return new Promise(lang.hitch(this, (resolve) => {
            if (this.flagReady) {
                resolve(true);
                return true;
            }

            this.waitForTrueInterval = setInterval(() => {
                if (this.flagReady) {
                    clearInterval(this.waitForTrueInterval);
                    resolve(true);
                }
            }, interval);
        }));
    }

    private authorName: string = "Guest";
    private async loadAuthorName(){
        this.authorName = await new Promise((resolve)=>{
            this.getCurrentUserName((username:string)=>{
                resolve(username);
            });
        });
        if ((this.authorName == null) || (this.authorName == "") || (typeof this.authorName != 'string')){
            this.authorName = "Guest";
        }

        if (this.config.stickyNotes.authorName == "Guest"){
            this.config.stickyNotes.authorName = this.authorName;
        }
    }

    /**
     * Ein Pdf Dokument aus einem base64 String laden.
     *
     * Anmerkung: sollte erst Aufgerufen werden, wenn der Container des pdfViewer Widgets bereits geladen ist,
     * da es bei aenderungen der Breite miss-alignments von Syncfusion kommen kann...
     * @param base64Data Pdf als Base64, darf Datatype Definition enthalten (`data:application/pdf;base64,`)
     */
    public async loadPdfByBase64(base64Data: string) {
        await this.waitForReady();
        //form export formats
        let formFormats: Object = window["ejs"]["pdfviewer"]["FormFieldDataFormat"];
        if (typeof this.formFieldsExportDataFormats != 'object'){
            this.formFieldsExportDataFormats = {};
        }
        for (let key in formFormats){
            this.formFieldsExportDataFormats[key] = formFormats[key];
        }
        //authoren name fuer comments und annotation laden
        await this.loadAuthorName();
        //base64 cleanup
        if (base64Data.indexOf('data:application/pdf;base64,') == -1) {
            base64Data = "data:application/pdf;base64," + base64Data; //mime type wird erwartet...
        }

        if (typeof this.config.experimental["disablePostingComments"] == 'boolean'){
            if (this.config.experimental["disablePostingComments"] == true){
                this.domNode.classList.add("disablePostingComments");
            }
        }
        //toolbar options setzen anhand config

        let toolbarOptions: Array<string | Object> = [];
        for (let option in this.config.ToolbarConfig) {
            if (this.config.ToolbarCustomButtons.hasOwnProperty(option) == false) {
                if (this.config.ToolbarConfig[option] == true) {
                    toolbarOptions.push(option);
                }
            } else {
                if (this.config.ToolbarConfig[option] == true) {
                    toolbarOptions.push(this.config.ToolbarCustomButtons[option].buttonDefinition);
                }
            }
        }

        //pdf viewer laden
        let pdfViewer = new window["ej"]["pdfviewer"]["PdfViewer"]({
            locale: "default_fallback",
            documentPath: base64Data,
            resourceUrl: window.location.origin + window.appRoot + 'Content/syncfusion/pdfReader/scripts/ej2-pdfviewer-lib', //render engine (wasm)
            toolbarSettings: { showTooltip: true, toolbarItems: toolbarOptions },
            stickyNotesSettings: { author: this.authorName },
            enableStickyNotesAnnotation: this.config.stickyNotes.enableStickyNotes,
            enablePageOrganizer: this.config.pageOrganizerSettings.enablePageOrganizer,
            pageOrganizerSettings: {
                canDelete: this.config.pageOrganizerSettings.canDelete,
                canInsert: this.config.pageOrganizerSettings.canInsert,
                canRotate: this.config.pageOrganizerSettings.canRotate,
                canCopy: this.config.pageOrganizerSettings.canCopy,
                canRearrange: this.config.pageOrganizerSettings.canRearrange,
                canImport: this.config.pageOrganizerSettings.canImport,
            }
        });
        window["ej"]["pdfviewer"]["PdfViewer"]["Inject"](window["ej"]["pdfviewer"]["TextSelection"], window["ej"]["pdfviewer"]["TextSearch"], window["ej"]["pdfviewer"]["Print"], window["ej"]["pdfviewer"]["Navigation"], window["ej"]["pdfviewer"]["Toolbar"], window["ej"]["pdfviewer"]["Magnification"], window["ej"]["pdfviewer"]["Annotation"], window["ej"]["pdfviewer"]["FormDesigner"], window["ej"]["pdfviewer"]["FormFields"], window["ej"]["pdfviewer"]["PageOrganizer"]);
        pdfViewer.appendTo('#' + this.pdfViewerUID);
        this.pdfViewer = pdfViewer;
        this.pdfViewer.measurementSettings = { scaleRatio: 2, conversionUnit: 'cm', displayUnit: 'cm' }; //natuerlich war burger-land default
        this.pdfViewer.annotationSettings.author = this.authorName;
        this.pdfViewer.stampSettings.author = this.authorName;
        this.pdfViewerInitHookEvents();
        this.pdfViewer.disableDefaultContextMenu = !this.config.enableRightClickMenu;
        this.emit("pdfReady");
    }

    private pdfViewerInitHookEvents() {
        this.pdfViewer.pageChange = lang.hitch(this, (page: any) => {
            this.documentState.currentPage = this.pdfViewer.currentPageNumber;
            this.emit("pageChange", this.pdfViewer.currentPageNumber);
        });
        this.pdfViewer.pageClick = lang.hitch(this, (args: any) => {
            this.emit("pageClick", ...args)
            if (this.youSignSignatureState == true){
                this.setYousignSignatureState(false);
                let p = args["pageNumber"];
                let x = Math.round(args["x"]); //FIXME - test if rounding is needed
                let y = Math.round(args["y"]);
                let w = 200;
                let h = 63;
                let newField:pdfTypes.yousignField = {
                    page:p,
                    height:h,
                    width:w,
                    x:x,
                    y:y
                };
                this.emit("yousignFieldAdded", newField);
                //cleanup, da das signatur feld tot ist, muessen wir es manuell aus dem dom reisen
            }
        });
        this.pdfViewer.pageRenderInitiate = lang.hitch(this, () => {
            this.emit("pageRenderInitiate");
        });
        this.pdfViewer.pageRenderComplete = lang.hitch(this, () => {
            this.documentState.maxPage = this.pdfViewer.pageCount;
            this.documentState.currentPage = this.pdfViewer.currentPageNumber;
            if (this.flagFirstLoad == true) {
                this.flagFirstLoad = false;
                this.emit("pageRenderCompleteFirstLoad");
                this.pdfViewer.magnification.fitToAuto();
                this.documentState.currentZoom = this.pdfViewer.zoomPercentage;
                //weil beim erst laden des dokumentes evtl. events bereits getriggered werden, obwohld der user noch nichts gemacht hat, warten wir bis das dokument auf jeden fall geladen hat
                this.eventHookTimeout = setTimeout(lang.hitch(this, this.pdfViewerInitHookEvents_onFirstPageLoad), 500);
            }
            this.emit("pageRenderComplete");
        });
        this.pdfViewer.zoomChange = lang.hitch(this, () => {
            this.documentState.currentZoom = this.pdfViewer.zoomPercentage;
            this.emit("zoomChange", this.documentState.currentZoom);
        });
        this.pdfViewer.onPropertyChanged = lang.hitch(this, async () => {
            this.syncFormExportToState()
            this.emit("propertyChanged");
        });
        this.pdfViewer.formFieldClick = lang.hitch(this, (clickedField: pdfTypes.formFieldEvent_FieldClick) => {
            this.syncFormExportToState();
            this.emit("formFieldClick", clickedField);
        });
        this.pdfViewer.formFieldDoubleClick = lang.hitch(this, (clickedField: pdfTypes.formFieldEvent_FieldClick) => {
            this.syncFormExportToState();
            this.emit("formFieldDoubleClick", clickedField);
        })
        this.pdfViewer.validateFormFields = lang.hitch(this, (validateFormFields: pdfTypes.FormFieldEvent_FieldValidationFailed) => {
            this.syncFormExportToState();
            this.emit("formFieldValidationFailed", validateFormFields);
        });
        this.pdfViewer.toolbarClick = lang.hitch(this, (args: any) => {
            //zum handlen von custom buttons
            this.emit("toolbarClick");
            for (let customButtonName in this.config.ToolbarCustomButtons) {
                if (args.item.properties.id === customButtonName) {
                    if (typeof this.config.ToolbarCustomButtons[customButtonName].callback == 'function') {
                        this.config.ToolbarCustomButtons[customButtonName].callback(args.originalEvent);
                    }
                }
            }
        });
        this.pdfViewer.downloadStart = lang.hitch(this, (args: { cancel: boolean }) => {
            args.cancel = this.config.cancelDownloads;
            this.emit("downloadStart", args);
        });
        this.pdfViewer.downloadEnd = lang.hitch(this, (args: { downloadDocument: any }) => {
            let binaryString = '';

            //uintarray to base64 string
            for (let i = 0; i < args.downloadDocument.length; i++) {
                binaryString += String.fromCharCode(args.downloadDocument[i]);
            }

            const base64Doc = btoa(binaryString);
            this.emit("downloadEnd", base64Doc);
        });

        this.pdfViewer.signatureSelect = lang.hitch(this, (args:any|null)=>{
            this.emit("signatureSelect", args);
        });
        this.pdfViewer.signatureUnselect = lang.hitch(this, (args:any|null)=>{
            this.emit("signatureUnselect", args);
        });
    }

    private async updateAnnotationState(){
        let newAnnotations = await this.pdfViewer.exportAnnotationsAsObject();
        this.documentState.annotations = newAnnotations;
    }

    private pdfViewerInitHookEvents_onFirstPageLoad() {
        //weil "annotationPropertiesChanged" wirklich nur triggered wenn ein bereits existierendes annotation property geaendert wird,
        //muessen wir uns sozusagen selber ein change event zusammen bauen...
        this.pdfViewer.annotationAdd = lang.hitch(this, async (args: any) => {
            await this.updateAnnotationState();
            this.emit("annotationsChanged", args);
            this.emit("annotationAdd", args);
        });
        this.pdfViewer.annotationMove = lang.hitch(this, async (args: any) => {
            await this.updateAnnotationState();
            this.emit("annotationsChanged", args);
            this.emit("annotationMove", args);
        });
        this.pdfViewer.annotationRemove = lang.hitch(this, async (args: any) => {
            await this.updateAnnotationState();
            this.emit("annotationsChanged", args);
            this.emit("annotationRemove", args);
        });
        this.pdfViewer.annotationResize = lang.hitch(this, async (args: any) => {
            await this.updateAnnotationState();
            this.emit("annotationsChanged", args);
            this.emit("annotationResize", args);
        });
        this.pdfViewer.annotationPropertiesChange = lang.hitch(this, async (args: any) => {
            await this.updateAnnotationState();
            this.emit("annotationsChanged", args);
            this.emit("annotationPropertiesChange", args);
        });
        this.pdfViewer.commentAdd = lang.hitch(this, async (args: any) => {
            await this.updateAnnotationState();
            this.emit("annotationsChanged", args);
            this.emit("commentAdd", args);
        });
        this.pdfViewer.commentDelete = lang.hitch(this, async (args: any) => {
            await this.updateAnnotationState();
            this.emit("annotationsChanged", args);
            this.emit("commentDelete", args);
        });
        this.pdfViewer.commentEdit = lang.hitch(this, async (args: any) => {
            await this.updateAnnotationState();
            this.emit("annotationsChanged", args);
            this.emit("commentEdit", args);
        });
        this.pdfViewer.commentStatusChanged = lang.hitch(this, async (args: any) => {
            await this.updateAnnotationState();
            this.emit("annotationsChanged", args);
            this.emit("commentStatusChanged", args);
        });
        this.pdfViewer.addSignature = lang.hitch(this, async (args: any) => {
            await this.updateAnnotationState();
            this.emit("annotationsChanged", args);
            this.emit("addSignature", args);
        });
        this.pdfViewer.removeSignature = lang.hitch(this, async (args: any) => {
            await this.updateAnnotationState();
            this.emit("annotationsChanged", args);
            this.emit("removeSignature", args);
        });
        this.pdfViewer.signaturePropertiesChange = lang.hitch(this, async (args: any) => {
            await this.updateAnnotationState();
            this.emit("annotationsChanged", args);
            this.emit("signaturePropertiesChange", args);
        });
        this.pdfViewer.formFieldPropertiesChange = lang.hitch(this, (changeField: pdfTypes.formFieldEvent_FieldChanged) => {
            this.syncFormExportToState();
            this.emit("formFieldPropertiesChange", changeField);
            this.emit("formFieldChanged");
        });
        this.pdfViewer.formFieldRemove = lang.hitch(this, (removedField: pdfTypes.FormFieldEvent_FieldRemove) => {
            this.syncFormExportToState();
            this.emit("formFieldRemove", removedField);
            this.emit("formFieldChanged");
        });
        this.pdfViewer.formFieldMove = lang.hitch(this, (args: any) => {
            this.emit("formFieldMove", args);
            this.emit("formFieldChanged");
        });
        this.pdfViewer.formFieldResize = lang.hitch(this, (args: any) => {
            this.emit("formFieldResize", args);
            this.emit("formFieldChanged");
        });
        this.pdfViewer.formFieldAdd = lang.hitch(this, (addedField: pdfTypes.formFieldEvent_FieldAdd) => {
            this.syncFormExportToState();
            this.emit("formFieldAdd", addedField);
            this.emit("formFieldChanged");
        });
        //#region war-crimes section
        // wir hooken uns hier in interne funktionen vom pdfViewer rein, weil es oft keine option zum disablen gibt bzw. events von manche funktionen
        // diese funktionen koennen evtl. bricken beim updaten vom pdf viewer...
        // danke dafuer syncFusion
        /*
        if (typeof this.pdfViewer["pageOrganizer"] != 'undefined') {
            this.pdfViewer.pageOrganizer.onSaveClicked = lang.hitch(this, (oldFun: Function) => {
                this.emit("pageOrganizer_saveClick");
                if (this.config.experimental.hasOwnProperty("disable_pageOrganizer_save_click")) {
                    if (this.config.experimental["disable_pageOrganizer_save_click"] == true) {
                        return;
                    }
                }
                oldFun.bind(this.pdfViewer.pageOrganizer)(); //setzt den scope zurueck auf pageOrganizer und weil wir auch nicht einmal args weitergeben muessen funktioniert das hier ganz gut
            }, this.pdfViewer.pageOrganizerModule.onSaveClicked);

            this.pdfViewer.pageOrganizer.onSaveasClicked = lang.hitch(this, (oldFun: Function) => {
                this.emit("pageOrganizer_saveAsClick");
                if (this.config.experimental.hasOwnProperty("disable_pageOrganizer_saveAs_click")) {
                    if (this.config.experimental["disable_pageOrganizer_saveAs_click"] == true) {
                        return;
                    }
                }
                oldFun.bind(this.pdfViewer.pageOrganizer)();
            }, this.pdfViewer.pageOrganizerModule.onSaveasClicked);
        }
        */

        if (this.config.experimental.hasOwnProperty("disable_pageOrganizer_saveAs_click")) {
            if (this.config.experimental["disable_pageOrganizer_saveAs_click"] == true) {
                this.domNode.classList.add("disable_pageOrganizer_saveAs_click");
            }
        }

        if (this.config.experimental["disable_annotation_import_export_menu_click"] == true){
            this.domNode.classList.add("disabledAnnotationImporExportMenuButton");
        }
        this.emit("allEventsReady");
    }

    private async syncFormExportToState() {
        if (this.flagFirstLoad == true) {
            try {
                this.documentState.formFieldsJson = await this.exportFormFields('Json', null);
            } catch {
                if (typeof this.documentState.formFieldsJson == 'undefined') {
                    this.documentState.formFieldsJson = "{}";
                }
                if ((this.documentState.formFieldsJson == null) || (this.documentState.formFieldsJson == "")) {
                    this.documentState.formFieldsJson = "{}";
                }
            }
        }
    }

    /**
     * Pdf Formular Felder Exportieren
     *
     * ACHTUNG: Neu Erstellte Felder werden nicht Exportiert, nur den Status von den Feldern wird Exportiert (also zeug das mit dem Form-Editor editiert wird, wird hier nicht exportiert)
     * @param format Export Format, Default ist Json
     * @param callback Callback fuer Sync
     * @returns {string} Des Exports in den gewaehlten Format
     */
    public async exportFormFields(format: 'Json' | 'Xfdf' | 'Fdf' = 'Json', callback: (data: string) => {} = null) {
        if (this.flagFirstLoad == false) {
            return "{}";
        }
        // https://help.syncfusion.com/document-processing/pdf/pdf-viewer/javascript-es5/form-designer/create-programmatically
        let data: string = "{}";
        try {
            //wir muessen hier die coolen null checks machen weil syncfusion es nicht macht...
            //diese werte sind im loadIn evtl. noch nicht vorhanden obwohl formFieldEvent_FieldChanged triggered.
            if (typeof this.pdfViewer.formFieldCollection != 'undefined'){
                if (typeof this.pdfViewer.formFieldsBase.formFieldLoadedDocument != 'undefined'){
                    if (typeof this.pdfViewer.formFieldsBase.formFieldLoadedDocument.form != 'undefined'){
                        data = await this.pdfViewer.exportFormFieldsAsObject(this.formFieldsExportDataFormats[format]);
                    }
                }
            }
        } catch {
            data = "{}";
        }
        if (typeof callback == 'function') {
            callback(data);
        }
        return data;
    }

    /**
    * Importiert Formularfelder in das PDF-Dokument.
    *
    * Diese Funktion kann nur die Werte von bestehenden Formularfeldern laden
    * und KEINE Felder anhand des Imports erstellen.
    *
    * @param {('Json' | 'Xfdf' | 'Fdf')} format - Das Format der importierten Daten. Standardmäßig ist es 'Json'.
    * @param {string} data - String vom Inhalt der Export-Datei.
    */
    public importFormFields(format: 'Json' | 'Xfdf' | 'Fdf' = 'Json', data: string) {
        if (typeof data == 'undefined') {
            return;
        }
        if ((typeof data != 'string') || (data == '') || (data == ' ') || (data == "{}") || (data == null)) {
            return; //nix zum importieren
        }
        try {
            this.pdfViewer.importFormFields(data, window["ej"]["pdfviewer"]["FormFieldDataFormat"][format]);
        } catch (err) {
            console.error("unable to import form fields", err);
            throw "unable to import";
        }
    }

    /**
     * Exportiert Seiten als Bild
     * @param startPageNumber
     * @param endPageNumber
     * @param imageWidth Export Bild-Breite
     * @param imageHeight Export Bild-Hoehe
     * @param callback - Callback fuer Sync
     * @returns {Array<string>} mit den exportierten Seiten als Bilder
     */
    public async exportAsImage(startPageNumber = 0, endPageNumber = this.documentState.maxPage, imageWidth: number, imageHeight: number, callback: (imageDetails: Array<string>) => {} = null): Promise<Array<string>> {
        let size = { height: imageHeight, width: imageWidth };
        let imageDetails: Array<string> = await this.pdfViewer.exportAsImages(startPageNumber, endPageNumber, size);
        if (typeof callback == 'function') {
            callback(imageDetails);
        }
        return imageDetails;
    }

    public clearAnnotations() {
        this.pdfViewer.deleteAnnotations();
    }

    /**
     * Speichert das aktuelle (editierte) Pdf Dokument als Blob Objekt
     * @param callback Callback fuer Sync
     * @returns {Blob} des Pdf-Dokumentes
     */
    public async saveAsBlob(callback: (blob: Blob) => {} = null) {
        let blob: Blob = await this.pdfViewer.saveAsBlob();
        if (typeof callback == 'function') {
            callback(blob);
        }
        return blob;
    }

    public print() {
        this.pdfViewer.print.print();
    }

    /**
     * Exportiert das Dokument als entweder Browser-Download oder als string
     *
     * Anmerkung: Wenn in der Pdf-Config Downloads deaktiviert wurden, dann Funktioniert diese Funktion nicht. Bitte daher in der Konfiguration Downloads als true setzen.
     *
     * @param downloadToMemory Ob ein Browser-Download getriggered werden soll oder das Dokument als String exportiert werden soll
     * @param callback Callback fuer Sync
     * @returns {string} des Dokumentes.
     */
    public async download(downloadToVariable: boolean = false,
        varExportType: 'Blob' | 'rawPdfString' | 'Base64String' = 'Base64String',
        callback?: ((downloadToMemContent: string | null | Blob) => void) | Function,
        fileNameWithExtension?: string): Promise<string | Blob | null> {

        if (downloadToVariable == true) {
            let res: any = null;
            const blob: Blob = await this.pdfViewer.saveAsBlob();
            if (varExportType == 'Blob') {
                res = blob;
            }
            if (varExportType == 'rawPdfString') {
                const text: string = await blob.text();
                res = text;
            }
            if (varExportType == 'Base64String') {
                const base64 = await new Promise<string>(resolve => {
                    const reader = new FileReader();
                    reader.onload = () => resolve((reader.result as string).split(',')[1]);
                    reader.readAsDataURL(blob);
                });
                res = base64;
            }
            if (typeof callback == 'function') {
                callback(res);
            }
            return res;
        } else {
            if (typeof(fileNameWithExtension) == "string"){
                this.pdfViewer["downloadFileName"] = fileNameWithExtension;
            }
            this.pdfViewer.download();
            if (typeof callback == 'function') {
                callback(null);
            }
            return null;
        }
    }

    /* public async loadAndShowTestFile() {
        let testFileUri = location.origin + window.appRoot + "Content/pdfReader_syncfusion/testfile.pdf";
        let response = await fetch(testFileUri);
        let buffer = await response.arrayBuffer();
        let binary = '';
        let bytes = new Int8Array(buffer);
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        let base64 = btoa(binary);
        return await this.loadPdfByBase64(base64);
    }*/

    /**
     * Setzt den Zoom anhand der Widget Breite.
     */
    public zoomFitToWidth() {
        this.pdfViewer.magnification.fitToWidth();
    }

    public getSyncFusionPdfViewer() {
        return this.pdfViewer;
    }

    /**
     * Ob ein YouSignSignature Feld gerade gesetzt wird
     */
    public setYousignSignatureState(setSignatureState:boolean){
        this.youSignSignatureState = setSignatureState;
        if (setSignatureState == true){
            //hack wegen syncfusion bug, remove foreign elements with id "undefined" (die bleiben sonst halbtransparent picken am TextLayer...)
            let foreignObject_htmlHelpers = this.domNode.querySelectorAll('[id="FormField_helper_html_element"].foreign-object')
            for (let elem of foreignObject_htmlHelpers as any as Array<HTMLDivElement>) {
                if (elem.querySelectorAll('[id="undefined"]').length > 0) {
                    //elem.remove(); //<- side effects...
                    this.pdfViewer.formDesigner.deleteFormField("undefined");
                }
            }
            //active

            //style setzen von btn
            let btnSelect = this.domNode.querySelectorAll(".YouSignBtnIsInactive");
            let parentElement:HTMLButtonElement;
            for (let btn of btnSelect as any as Array<HTMLSpanElement>){
                btn.classList.remove("YouSignBtnIsInactive");
                btn.classList.add("YouSignBtnIsActive");
                parentElement = btn.parentElement as HTMLButtonElement;
            }
            this.domNode.classList.add("disablePdfToolBarBtns");
            //text setzen
            let textNode = parentElement.querySelector(".e-tbar-btn-text");
            textNode.innerHTML = this.translate("PdfViewer__CustomButton_YouSign_Place_Cancel");

            this.pdfViewer.toolbar.formDesignerToolbarModule.clickSignature();

        }else{
            //inactive

            //um das setzen vom feld abzubrechen (syncfusion)
            let keyEvent = new KeyboardEvent('keydown', {
                key: 'Escape',
                keyCode: 27,
                code: 'Escape',
                bubbles: true,
                cancelable: true
            });
            this.domNode.dispatchEvent(keyEvent);

            //style setzen von btn
            let btnSelect = this.domNode.querySelectorAll(".YouSignBtnIsActive");
            let parentElement: HTMLButtonElement;
            for (let btn of btnSelect as any as Array<HTMLButtonElement>) {
                btn.classList.remove("YouSignBtnIsActive");
                btn.classList.add("YouSignBtnIsInactive");
                parentElement = btn.parentElement as HTMLButtonElement;
            }
            this.domNode.classList.remove("disablePdfToolBarBtns");
            //text setzen
            let textNode = parentElement.querySelector(".e-tbar-btn-text");
            textNode.innerHTML = this.translate("PdfViewer__CustomButton_YouSign_Place");



            //hack wegen syncfusion bug, remove foreign elements with id "undefined" (die bleiben sonst halbtransparent picken am TextLayer...)
            let foreignObject_htmlHelpers = this.domNode.querySelectorAll('[id="FormField_helper_html_element"].foreign-object')
            for (let elem of foreignObject_htmlHelpers as any as Array<HTMLDivElement>){
                if (elem.querySelectorAll('[id="undefined"]').length > 0){
                    //elem.remove(); //<- side effects...
                    this.pdfViewer.formDesigner.deleteFormField("undefined");
                }
            }
        }
    }
    public yousign_addSigBtn_disabled(isDisabled:boolean){
        let child = this.domNode.querySelector(".YouSignBtnIsInactive");
        if (typeof child == 'undefined' || child == null){
            console.error("youSign button doesnt exist...");
            this.emit("yousignMobileModeDetected", true);
            return;
        }
        let btn:HTMLButtonElement = child.parentElement as HTMLButtonElement;
        btn.disabled = isDisabled;
        if (isDisabled){
            btn.classList.add("w3-disabled");
            btn.parentElement.classList.add("w3-disabled");
        }else{
            btn.classList.remove("w3-disabled");
            btn.parentElement.classList.remove("w3-disabled");
        }
    }
    startup(): void {
        super.startup.apply(this, arguments as any);
    }

    destroy(): void {
        clearInterval(this.waitForTrueInterval);
        clearTimeout(this.eventHookTimeout);
    }

    //NOTE - https://ej2.syncfusion.com/staging/documentation/pdfviewer/globalization/
}

export default mdsWidget(pdfViewer);
